import type { BenchmarkRecord } from '../../models/benchmark.types'

import type { ReportRow } from '@/features/reports'

/**
 * Enrich controller rows with TikTok percentile data without mutating either input.
 *
 * The custom endpoint groups by `info.ad_id`. Information fields stay flat so they can satisfy
 * the report's selected dimensions, while percentile metrics retain the controller's `p_` naming
 * convention. Unmatched report rows remain value-identical but still receive a fresh object so a
 * caller can safely treat the result as an immutable snapshot.
 */
export function mergeBenchmarkRows(
  rows: readonly ReportRow[],
  benchmark: readonly BenchmarkRecord[],
): ReportRow[] {
  if (benchmark.length === 0) return rows.map((row) => ({ ...row }))

  const byAdId = new Map<string, BenchmarkRecord>()
  for (const record of benchmark) byAdId.set(String(record.info.ad_id), record)

  return rows.map((row) => {
    const match = byAdId.get(String(row['ad_id']))
    if (!match) return { ...row }

    const metrics = Object.fromEntries(
      Object.entries(match.metrics).map(([field, value]) => [`p_${field}`, value]),
    )
    return { ...row, ...match.info, ...metrics }
  })
}
